/**
 * Navegación: agrupa las pantallas de `screens.ts` por sección y las filtra por rol
 * (matriz RN-ROL-02 + `roleAtLeast` de `@viva/shared`).
 *
 * La sidebar del **portal admin** ya no sale de acá sino de `groups.ts` (grupos de trabajo
 * con páginas de tarjetas, 22-sep-2026). `navFor` sigue armando el menú del portal
 * profesores, que tiene pocas pantallas y no necesita grupos.
 *
 * Dos reglas del proyecto que este archivo aplica:
 * - **Sólo se listan las pantallas `built`.** Una pendiente tiene ruta (que explica qué
 *   módulo la construye) pero no ítem de menú: un menú lleno de callejones sin salida no
 *   es un mapa, es ruido.
 * - **El gating es cosmético** (Arquitectura §3.2): esconder un ítem no protege nada. Lo
 *   que protege es la API, que valida rol + sede asignada + sede activa y audita el
 *   intento denegado.
 */
import { roleAtLeast, type UserRole } from "@viva/shared";
import type { Area } from "../lib/auth/surface.js";
import { menuFor } from "./groups.js";
import { SECTIONS, SECTION_ORDER, type SectionDef, type SectionId } from "./sections.js";
import { SCREENS, type ScreenDef } from "./screens.js";

export interface NavItem {
  id: string;
  label: string;
  to: string;
  purpose: string | undefined;
}

export interface NavGroup {
  section: SectionDef;
  items: NavItem[];
}

/** ¿Este rol alcanza para ver esta pantalla? (gating cosmético, RN-ROL-02). */
export function canSeeScreen(screen: ScreenDef, role: UserRole): boolean {
  if (screen.roles) return screen.roles.includes(role);
  if (screen.minRole) return roleAtLeast(role, screen.minRole);
  // Sin restricción declarada: la ve cualquier sesión de staff.
  return true;
}

/** Menú de un área para un rol. Vacío para un rol sin nada que ver: no se inventa un ítem. */
export function navFor(area: Area, role: UserRole): NavGroup[] {
  const visible = SCREENS.filter(
    (screen) =>
      screen.area === area &&
      screen.status === "built" &&
      !screen.hidden &&
      canSeeScreen(screen, role),
  );

  const groups: NavGroup[] = [];
  for (const sectionId of SECTION_ORDER) {
    const items = visible
      .filter((screen) => screen.section === sectionId)
      .map((screen) => ({
        id: screen.id,
        label: screen.title,
        to: screen.path,
        purpose: screen.purpose,
      }));
    if (items.length > 0) groups.push({ section: SECTIONS[sectionId as SectionId], items });
  }
  return groups;
}

/**
 * Cuál de los ítems del menú queda marcado para una URL. Devuelve el `to` ganador, o
 * `null` si ninguno aplica.
 *
 * `NavLink` decide ítem por ítem, y así **cada** ruta que sea prefijo de la URL se marca
 * activa: en `/admin/socios/alta` se prendían «Padrón de socios» y «Alta presencial de
 * socio» a la vez. Hay 9 rutas del menú que son prefijo de otra (socios, personal, planes,
 * caja, migración, kiosko…), así que pasaba en media aplicación.
 *
 * Acá gana **el prefijo más largo**, y por eso queda exactamente uno. De paso resuelve el
 * caso que un `end` a secas rompería: las pantallas que no están en el menú porque cuelgan
 * de un id (`/admin/socios/<id>`, `/admin/personal/liquidaciones/<id>`) dejan marcada la
 * sección de la que salieron, que es donde el usuario cree que está.
 *
 * El corte es por segmento: `/admin/socios` no matchea `/admin/socios-viejos`.
 */
export function activeNavPath(paths: readonly string[], pathname: string): string | null {
  const url = pathname.replace(/\/+$/, "") || "/";
  let best: string | null = null;
  let bestLength = -1;
  for (const path of paths) {
    const candidate = path.replace(/\/+$/, "") || "/";
    // La raíz del área (`/admin`, `/profes`) es prefijo de TODO: sólo cuenta si la URL es
    // exactamente ella. Si no, la ficha de una clase (que no está en el menú) dejaría
    // marcado «Dashboard del dueño», y una ruta inexistente también.
    const isAreaRoot = candidate.split("/").filter(Boolean).length === 1;
    const matches = url === candidate || (!isAreaRoot && url.startsWith(`${candidate}/`));
    if (matches && candidate.length > bestLength) {
      best = path;
      bestLength = candidate.length;
    }
  }
  return best;
}

/**
 * La pantalla a la que entra cada rol al iniciar sesión.
 *
 * No es una lista escrita a mano: se resuelve contra el registro, así **nunca** se manda a
 * alguien a una pantalla que su rol no puede abrir (si eso pasara, el usuario entraría
 * directo a «sin permiso»). El orden de preferencia es el del menú.
 */
export function landingFor(role: UserRole): string {
  if (role === "teacher") return "/profes";
  // Dueño y Administración entran al dashboard del dueño (RN-REP-02).
  const dashboard = SCREENS.find((screen) => screen.id === "dashboard");
  if (dashboard && canSeeScreen(dashboard, role)) return dashboard.path;

  // El resto entra al primer grupo del menú que tiene algo para su rol (la recepción, al
  // Mostrador). Un grupo con una sola pantalla lleva directo a esa pantalla.
  const first = menuFor(role)[0]?.to;
  return first ?? "/admin/mi-cuenta";
}

/** Todas las rutas construidas que un rol puede abrir. Sirve al test de nav filtrada. */
export function allowedPathsFor(role: UserRole): string[] {
  const screens = SCREENS.filter(
    (screen) => screen.status === "built" && canSeeScreen(screen, role),
  ).map((screen) => screen.path);
  // Las páginas de tarjetas de los grupos del menú también son rutas que el rol abre.
  const hubs = menuFor(role).map((group) => `/admin/seccion/${group.group.id}`);
  return [...screens, ...hubs];
}
